import { useEffect, useState } from 'react';
import { Canvas, Rect } from 'fabric';
import { nanoid } from 'nanoid';

import { DrawData } from './draw.constants';
import { addArtboard } from './canvas/logic/draw-objects';
import {
  initialiseAndPositionCanvas,
  reviveSavedObjects,
} from './canvas/logic/draw-revive';
import { getMergedDrawObjects } from './canvas/logic/get-merged-draw-objects';
import { fitContentToCanvas, getObjectsBoundingBox } from './canvas/draw.utils';
import { grey1 } from 'approot/shared/styles/colours.style';
import * as Styled from './draw-canvas-preview.style';
import { useDrawFontLoader } from './canvas/use-draw-font-loader';
import { LanguageCodes } from 'global/constants';

/**
 * Frames the whole drawing (image + every stroke, including strokes outside the
 * image area) by growing the artboard to enclose all objects and reusing the
 * standard {@link fitContentToCanvas}. Objects are already attached to the
 * canvas, so their bounding rects are accurate.
 */
const fitToAllObjects = (canvas: Canvas, artboard: Rect) => {
  const bounds = getObjectsBoundingBox(
    canvas.getObjects().map(object => object.getBoundingRect())
  );
  if (!bounds) {
    return;
  }

  artboard.set(bounds);
  artboard.setCoords();
  fitContentToCanvas(canvas, artboard);
};

type Props = {
  id: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  containerEl: HTMLElement;
  answer?: DrawData;
  questionData?: DrawData | null;
  language: LanguageCodes;
  /**
   * When true, the viewport fits the union bounding box of every object (image +
   * all strokes) instead of the artboard, so strokes drawn outside the image area
   * are not clipped. Defaults to the artboard-framed behaviour.
   */
  fitToContent?: boolean;
};

const DrawCanvasPreview = ({
  id,
  imageUrl,
  dimensionsData,
  answer,
  questionData,
  containerEl,
  language,
  fitToContent = false,
}: Props) => {
  const [isInitialised, setInitialised] = useState<boolean>(false);

  const CANVAS_CONTAINER_ID = `inline-${id}-canvas-container`;
  const FABRIC_ELEMENT_ID = `inline-fabric-${id}-${nanoid()}-canvas-container`;

  useEffect(() => {
    const container = document.getElementById(CANVAS_CONTAINER_ID);
    const canvas = new Canvas(FABRIC_ELEMENT_ID, {
      selection: false,
      width: containerEl.offsetWidth,
      height: containerEl.offsetHeight,
      preserveObjectStacking: true,
    });

    const artboard = addArtboard(canvas, dimensionsData);
    artboard.stroke = grey1;

    const combinedData = getMergedDrawObjects(answer, questionData);

    // Re-fit to all content once the background image has loaded, so it's
    // included in the bounds. Positioning is otherwise fire-and-forget.
    const positionAndFit = () => {
      const positioned = initialiseAndPositionCanvas(
        canvas,
        dimensionsData,
        imageUrl,
        artboard
      );
      if (fitToContent) {
        positioned.then(() => fitToAllObjects(canvas, artboard));
      }
      setInitialised(true);
    };

    if (combinedData?.objects.length) {
      reviveSavedObjects({
        canvas,
        artboard,
        drawingData: combinedData,
        isCreateMode: false,
        language,
      }).then(positionAndFit);
    } else {
      positionAndFit();
    }

    const onResize = () => {
      if (container) {
        canvas.setDimensions({
          width: containerEl.offsetWidth,
          height: containerEl.offsetHeight,
        });

        if (fitToContent) {
          fitToAllObjects(canvas, artboard);
        } else {
          const ratio = containerEl.offsetWidth / dimensionsData.width;
          canvas.setViewportTransform([ratio, 0, 0, ratio, 0, 0]);
        }
      }
    };

    onResize();

    window.addEventListener('resize', onResize, false);
    return () => {
      window.removeEventListener('resize', onResize);
    };
    //eslint-disable-next-line
  }, []);

  return (
    <Styled.DrawCanvasPreview
      id={CANVAS_CONTAINER_ID}
      $isInitialised={isInitialised}
    >
      <canvas id={FABRIC_ELEMENT_ID}></canvas>
    </Styled.DrawCanvasPreview>
  );
};

const DrawCanvasPreviewWrapper = (props: Props) => {
  const isFontsLoaded = useDrawFontLoader();
  return isFontsLoaded ? <DrawCanvasPreview {...props} /> : null;
};

export default DrawCanvasPreviewWrapper;
